import { useCallback, useEffect, useRef, useState } from 'react';
import { focusDesktopWindow } from '../lib/desktop';
import { playNotificationSound, setSoundVolume } from '../lib/sound';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification as AppNotification, NotificationPrefs } from '@avslack/shared';
import { apiFetch } from '../lib/api';
import { pushDeliversHere } from '../lib/push-device';

export const notifyPrefsKey = ['notification-prefs'] as const;

export function useNotificationPrefs() {
  return useQuery({
    queryKey: notifyPrefsKey,
    queryFn: () =>
      apiFetch<{ prefs: NotificationPrefs }>('/api/notifications/prefs').then((r) => r.prefs),
    staleTime: 5 * 60_000,
  });
}

/**
 * A beallitott hangero atadasa a lejatszonak.
 *
 * A hangokat olyan helyekrol is inditjuk, ahol nincs komponens-fa (ertesites-
 * kezelo, emlekezteto-busz), ezert a `lib/sound.ts` modul-szintu allapotban
 * tartja a szorzot. Ez a horog az egyetlen hely, ami osszekoti a ketto -
 * egyszer, az alkalmazas gyokerenel hivjuk.
 */
export function useSoundVolume(): void {
  const { data: prefs } = useNotificationPrefs();
  const volume = prefs?.soundVolume;

  useEffect(() => {
    /*
      A beallitas BETOLTESEIG nem nyulunk hozza.

      Az alapertelmezes amugy is 100, tehat nincs mit allitani - egy koran
      kiirt nulla viszont lenemitana az elso ertesitest annak is, aki sosem
      huzta lejjebb a csuszkat.
    */
    if (volume === undefined) return;
    setSoundVolume(volume);
  }, [volume]);
}

export function useUpdateNotificationPrefs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<NotificationPrefs>) =>
      apiFetch<{ prefs: NotificationPrefs }>('/api/notifications/prefs', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }).then((r) => r.prefs),
    onSuccess: (prefs) => queryClient.setQueryData(notifyPrefsKey, prefs),
  });
}

export type BrowserPermission = 'default' | 'granted' | 'denied' | 'unsupported';

/**
 * Rendszerszintu ertesitesek.
 *
 * A DONTEST (kinek szoljon) a szerver hozza; itt csak az a kerdes, hogy
 * rendszer-ertesites kell-e. Ha a ful elorben van, nem zavarunk fel senkit
 * egy kulon ablakkal - a feluleten belul ugyis latszik az uzenet.
 */
export function useDesktopNotifications(onOpen: (channelId: string, messageId: string) => void) {
  const [permission, setPermission] = useState<BrowserPermission>(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );
  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  const request = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    const result = await Notification.requestPermission();
    setPermission(result);
  }, []);

  const show = useCallback((notification: AppNotification) => {
    const elorben = document.visibilityState === 'visible' && document.hasFocus();

    /*
      OLELES: a hang akkor is szol, ha az alkalmazas elorben van.

      Egy uzenetnel ez helyes viselkedes: aki a kepernyot nezi, latja az uj
      sort, folosleges rapittyegni. Egy hivas viszont MAS csatornaban indul,
      mint amit eppen nezunk, es a jelzese eddig egyetlen halvany pottyocska
      volt az oldalsavban - vagyis gyakorlatilag semmi. Raadasul a hivas
      idohoz kotott: aki tiz perc mulva veszi eszre, annak mar nincs hova
      belepnie.

      Rendszer-ertesitest viszont NEM nyitunk: aki a felulet elott ul, annak
      a hang es az oldalsav jelzese egyutt eleg.
    */
    if (elorben && notification.reason === 'huddle') {
      if (notification.sound) playNotificationSound(notification.soundName);
      return;
    }

    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // A ful elorben van: nem nyitunk kulon ablakot.
    if (elorben) return;

    /*
      HATTERBEN, push-ra feliratkozott eszkozon a push hozza el.

      Ilyenkor a szerver erre az eszkozre is kuld push-t, es ha a lap meg
      eletben van, a WebSocketen is megkapja ugyanazt. A kozos `tag` ezt
      elvileg osszevonna - az iOS viszont nem vonja ossze, es minden uzenet
      ketszer jelent meg. Lasd `lib/push-device.ts`.

      A LATHATO, de nem fokuszalt ablakra (masik ablak van folotte) a szerver
      nem kuld push-t, ott tovabbra is mi jelzunk.
    */
    if (document.visibilityState === 'hidden' && pushDeliversHere()) return;

    const where =
      notification.channelKind === 'dm' || notification.channelKind === 'group_dm'
        ? 'közvetlen üzenet'
        : `#${notification.channelName ?? ''}`;

    const title = `${notification.authorName} · ${where}`;
    const options: NotificationOptions = {
      body: notification.message.text.slice(0, 240),
      /*
        UGYANAZ a cimke, mint amit a push kuld (`ch-<csatorna>`).

        Egy elrejtett ablak ketfele is ertesulhet ugyanarrol az uzenetrol: push
        erkezik ra, es amikor a bongeszo felengedi a lapot, a WebSocketen allo
        ertesites is lefut. Kozos cimkevel a masodik FELULIRJA az elsot, nem
        pedig melle all - ahogy tobb nyitott ful eseten is egy jelzes marad.
      */
      tag: `ch-${notification.channelId}`,
      // A rendszer sajat hangja helyett a SAJAT valasztott hangunk szol.
      silent: true,
    };

    if (notification.sound) playNotificationSound(notification.soundName);

    /*
      ANDROIDON a `new Notification()` HIBAT DOB ("Illegal constructor"): ott
      csak a service worker jelezhet. Ilyenkor ugyanazzal a cellal (csatorna,
      uzenet) a service workeren at jelenik meg - a kattintast a `sw.js`
      intezi, mint a push-nal.
    */
    let native: Notification;
    try {
      native = new Notification(title, options);
    } catch {
      void navigator.serviceWorker?.ready
        .then((registration) =>
          registration.showNotification(title, {
            ...options,
            data: { channelId: notification.channelId, messageId: notification.message.id },
          }),
        )
        .catch(() => undefined);
      return;
    }

    native.addEventListener('click', () => {
      window.focus();
      // Desktopon a talcara rejtett ablakot a `window.focus()` nem hozza elo -
      // ott a natív oldalnak kell megmutatnia. Bongeszoben ez nem csinal semmit.
      void focusDesktopWindow();
      openRef.current(notification.channelId, notification.message.id);
      native.close();
    });
  }, []);

  useEffect(() => {
    if (typeof Notification !== 'undefined') setPermission(Notification.permission);
  }, []);

  return { permission, request, show };
}
